'use client';

import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock,
  CheckCircle,
  XCircle,
  MessageSquare,
  FileText,
  Image as ImageIcon,
  MapPin,
  Calendar,
  Star,
  AlertTriangle,
  User,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import Modal from '@/components/ui/Modal';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';



interface VerificationApplication {
  id: string;
  user_id: string | null;
  name: string;
  email: string;
  city: string;
  specialty: string;
  submitted_at: string;
  hours_waiting: number;
  has_national_id: boolean;
  has_trade_license: boolean;
  portfolio_photos_count: number;
  has_selfie: boolean;
  bio: string;
  experience: string;
  price_range: string;
  languages: string[];
  is_overdue: boolean;
  status: string;
}

interface RejectModalData {
  open: boolean;
  applicationId: string;
  name: string;
  reason: string;
}

interface InfoModalData {
  open: boolean;
  applicationId: string;
  name: string;
  message: string;
}

function EyeIcon({ size, className }: { size: number; className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function docScore(app: VerificationApplication) {
  let score = 0;
  if (app.has_national_id) score++;
  if (app.has_trade_license) score++;
  if (app.portfolio_photos_count >= 4) score++;
  if (app.has_selfie) score++;
  return score;
}

export default function VerificationQueueContent() {
  const [applications, setApplications] = useState<VerificationApplication[]>([]);
  const [selected, setSelected] = useState<VerificationApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [rejectModal, setRejectModal] = useState<RejectModalData>({
    open: false,
    applicationId: '',
    name: '',
    reason: '',
  });
  const [infoModal, setInfoModal] = useState<InfoModalData>({
    open: false,
    applicationId: '',
    name: '',
    message: '',
  });
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const fetchApplications = useCallback(async () => {
    const supabase = createClient();
    const { data } = await supabase
      .from('artisan_verifications')
      .select('*')
      .eq('status', 'under_review')
      .order('submitted_at', { ascending: true });
    const apps = data ?? [];
    setApplications(apps);
    if (apps.length > 0 && !selected) setSelected(apps[0]);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleApprove = async (id: string, name: string) => {
    setProcessingId(id);
    const supabase = createClient();
    const { error } = await supabase
      .from('artisan_verifications')
      .update({ status: 'verified', reviewed_at: new Date().toISOString() })
      .eq('id', id);

    if (!error && selected?.user_id) {
      await supabase
        .from('user_profiles')
        .update({ verification_status: 'verified', status: 'active' })
        .eq('id', selected.user_id);
    }

    if (error) {
      toast.error('Failed to approve. Please try again.');
    } else {
      toast.success(`${name} has been approved as a verified artisan`);
      await fetchApplications();
      const remaining = applications.filter((a) => a.id !== id);
      setSelected(remaining.length > 0 ? remaining[0] : null);
    }
    setProcessingId(null);
  };

  const handleReject = async () => {
    setProcessingId(rejectModal.applicationId);
    const supabase = createClient();
    const { error } = await supabase
      .from('artisan_verifications')
      .update({
        status: 'unverified',
        rejection_reason: rejectModal.reason,
        reviewed_at: new Date().toISOString(),
      })
      .eq('id', rejectModal.applicationId);

    if (error) {
      toast.error('Failed to reject. Please try again.');
    } else {
      toast.error(`${rejectModal.name} application rejected`);
      await fetchApplications();
      const remaining = applications.filter((a) => a.id !== rejectModal.applicationId);
      setSelected(remaining.length > 0 ? remaining[0] : null);
    }
    setProcessingId(null);
    setRejectModal({ open: false, applicationId: '', name: '', reason: '' });
  };

  const handleRequestInfo = async () => {
    toast.success(`Information request sent to ${infoModal.name}`);
    setInfoModal({ open: false, applicationId: '', name: '', message: '' });
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-GB');

  if (loading) {
    return (
      <div className="px-6 py-6 max-w-screen-2xl mx-auto">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={`stat-skel-${i}`} className="card-elevated rounded-xl px-4 py-3 h-16 animate-pulse bg-muted" />
          ))}
        </div>
        <div className="flex gap-5 h-[calc(100vh-280px)] min-h-[600px]">
          <div className="w-80 card-elevated rounded-xl animate-pulse bg-muted" />
          <div className="flex-1 card-elevated rounded-xl animate-pulse bg-muted" />
        </div>
      </div>
    );
  }

  const overdueCount = applications.filter((a) => a.is_overdue).length;
  const incompleteCount = applications.filter((a) => docScore(a) < 4).length;
  const avgWait =
    applications.length > 0
      ? Math.round(applications.reduce((s, a) => s + (a.hours_waiting ?? 0), 0) / applications.length)
      : 0;

  return (
    <div className="px-6 py-6 max-w-screen-2xl mx-auto">
      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Pending', value: applications.length.toString(), color: 'text-foreground' },
          { label: 'Overdue (>48h)', value: overdueCount.toString(), color: 'text-red-600' },
          { label: 'Docs Incomplete', value: incompleteCount.toString(), color: 'text-amber-600' },
          { label: 'Avg Wait', value: `${avgWait}h`, color: 'text-foreground' },
        ].map((stat) => (
          <div key={`vstat-${stat.label}`} className="card-elevated rounded-xl px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1">{stat.label}</p>
            <p className={`text-2xl font-700 font-tabular ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-5 h-[calc(100vh-280px)] min-h-[600px]">
        {/* Queue list */}
        <div className="w-80 shrink-0 card-elevated rounded-xl overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-border bg-muted/40">
            <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide">
              Pending Queue · {applications.length} applications
            </p>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-thin divide-y divide-border/60">
            {applications.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-12 px-4 text-center">
                <CheckCircle size={32} className="text-accent mb-3" />
                <p className="text-sm font-600 text-foreground mb-1">Queue cleared!</p>
                <p className="text-xs text-muted-foreground">All artisan applications have been reviewed.</p>
              </div>
            ) : (
              applications.map((app) => {
                let score = docScore(app);
                const isSelected = selected?.id === app.id;
                return (
                  <button
                    key={app.id}
                    onClick={() => setSelected(app)}
                    className={`w-full text-left px-4 py-3.5 transition-colors ${
                      isSelected ? 'bg-primary/5 border-r-2 border-primary' : 'hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-sm font-600 text-foreground truncate">{app.name}</p>
                      {app.is_overdue && (
                        <span className="shrink-0 flex items-center gap-0.5 text-xs text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full">
                          <AlertTriangle size={10} />
                          Late
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      {app.specialty} · {app.city}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock size={10} />
                        {app.hours_waiting}h ago
                      </div>
                      <div className="flex items-center gap-0.5">
                        {[...Array(4)].map((_, i) => (
                          <div
                            key={`doc-dot-${app.id}-${i}`}
                            className={`w-1.5 h-1.5 rounded-full ${i < score ? 'bg-accent' : 'bg-border'}`}
                          />
                        ))}
                        <span className="text-xs text-muted-foreground ml-1">{score}/4 docs</span>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Detail panel */}
        {selected ? (
          <div className="flex-1 card-elevated rounded-xl overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-border flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User size={18} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-base font-700 text-foreground">{selected.name}</h2>
                    <p className="text-xs text-muted-foreground">{selected.email}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {selected.is_overdue && (
                  <span className="flex items-center gap-1 text-xs font-600 text-red-700 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">
                    <AlertTriangle size={11} />
                    Overdue — {selected.hours_waiting}h waiting
                  </span>
                )}
                <StatusBadge status="under_review" />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-thin p-6 space-y-6">
              {/* Profile info grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: 'Specialty', value: selected.specialty, icon: Star },
                  { label: 'City', value: selected.city, icon: MapPin },
                  { label: 'Experience', value: selected.experience, icon: Clock },
                  { label: 'Price Range', value: selected.price_range, icon: ChevronRight },
                ].map((info) => {
                  const InfoIcon = info.icon;
                  return (
                    <div key={`info-${info.label}`} className="bg-muted rounded-lg px-3 py-3">
                      <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                        <InfoIcon size={11} />
                        {info.label}
                      </p>
                      <p className="text-sm font-600 text-foreground">{info.value}</p>
                    </div>
                  );
                })}
              </div>

              {/* Bio */}
              <div>
                <h3 className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">About</h3>
                <p className="text-sm text-foreground leading-relaxed bg-muted/40 rounded-lg p-4">{selected.bio}</p>
              </div>

              {/* Languages */}
              <div>
                <h3 className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">Languages</h3>
                <div className="flex items-center gap-2">
                  {selected.languages?.map((lang) => (
                    <span
                      key={`lang-${selected.id}-${lang}`}
                      className="text-xs font-500 bg-primary/8 text-primary px-2.5 py-1 rounded-full"
                    >
                      {lang}
                    </span>
                  ))}
                </div>
              </div>

              {/* Documents checklist */}
              <div>
                <h3 className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-3">Document Verification</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'nationalId', label: 'National ID (Hawiya)', present: selected.has_national_id, required: true },
                    { key: 'tradeLicense', label: 'Trade License / Sijil Tijari', present: selected.has_trade_license, required: false },
                    { key: 'portfolio', label: `Portfolio Photos (${selected.portfolio_photos_count} uploaded)`, present: selected.portfolio_photos_count >= 3, required: true },
                    { key: 'selfie', label: 'Identity Selfie', present: selected.has_selfie, required: true },
                  ].map((doc) => (
                    <div
                      key={`doc-${selected.id}-${doc.key}`}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        doc.present
                          ? 'bg-emerald-50 border-emerald-200'
                          : doc.required
                          ? 'bg-red-50 border-red-200' :'bg-amber-50 border-amber-200'
                      }`}
                    >
                      {doc.present ? (
                        <CheckCircle size={16} className="text-emerald-600 shrink-0" />
                      ) : (
                        <XCircle size={16} className={`shrink-0 ${doc.required ? 'text-red-600' : 'text-amber-600'}`} />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-600 ${doc.present ? 'text-emerald-700' : doc.required ? 'text-red-700' : 'text-amber-700'}`}>
                          {doc.label}
                        </p>
                        {!doc.present && (
                          <p className="text-xs text-muted-foreground">
                            {doc.required ? 'Required — missing' : 'Recommended — not provided'}
                          </p>
                        )}
                      </div>
                      {doc.present && (
                        <button
                          onClick={() => setLightboxOpen(true)}
                          className="shrink-0 p-1 rounded text-emerald-600 hover:bg-emerald-100 transition-colors"
                          title="View document"
                        >
                          <EyeIcon size={13} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Portfolio preview */}
              <div>
                <h3 className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-3">
                  Portfolio Preview ({selected.portfolio_photos_count} photos)
                </h3>
                <div className="grid grid-cols-4 gap-2">
                  {Array.from({ length: Math.min(selected.portfolio_photos_count, 8) }).map((_, i) => (
                    <button
                      key={`portfolio-${selected.id}-${i + 1}`}
                      onClick={() => setLightboxOpen(true)}
                      className="aspect-square rounded-lg bg-muted border border-border flex items-center justify-center hover:bg-muted/80 transition-colors group overflow-hidden"
                    >
                      <ImageIcon size={20} className="text-muted-foreground group-hover:text-foreground transition-colors" />
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">Click any photo to open document viewer</p>
              </div>

              {/* Submitted date */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar size={13} />
                Application submitted on {formatDate(selected.submitted_at)} · {selected.hours_waiting} hours in queue
              </div>
            </div>

            {/* Action footer */}
            <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center gap-3">
              <button
                onClick={() => handleApprove(selected.id, selected.name)}
                disabled={processingId === selected.id}
                className="btn-accent flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {processingId === selected.id ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <CheckCircle size={15} />
                )}
                Approve Artisan
              </button>

              <button
                onClick={() =>
                  setRejectModal({ open: true, applicationId: selected.id, name: selected.name, reason: '' })
                }
                className="btn-danger flex items-center gap-2"
              >
                <XCircle size={15} />
                Reject
              </button>

              <button
                onClick={() =>
                  setInfoModal({ open: true, applicationId: selected.id, name: selected.name, message: '' })
                }
                className="btn-ghost border border-border flex items-center gap-2"
              >
                <MessageSquare size={15} />
                Request More Info
              </button>

              <div className="flex-1" />
              <button className="btn-ghost flex items-center gap-2">
                <FileText size={15} />
                View Full Profile
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 card-elevated rounded-xl flex items-center justify-center">
            <div className="text-center">
              <CheckCircle size={40} className="text-accent mx-auto mb-3" />
              <p className="text-base font-600 text-foreground mb-1">Select an application</p>
              <p className="text-sm text-muted-foreground">
                Choose an artisan from the queue to review their documents.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      <Modal
        open={rejectModal.open}
        onClose={() => setRejectModal({ open: false, applicationId: '', name: '', reason: '' })}
        title="Reject Application"
        size="md"
      >
        <p className="text-sm text-muted-foreground mb-4">
          Rejecting <span className="font-600 text-foreground">{rejectModal.name}</span>. Please provide a reason.
        </p>
        <div className="mb-5">
          <label className="block text-sm font-600 text-foreground mb-1.5">
            Rejection reason <span className="text-red-500">*</span>
          </label>
          <textarea
            rows={4}
            value={rejectModal.reason}
            onChange={(e) => setRejectModal((p) => ({ ...p, reason: e.target.value }))}
            placeholder="e.g. Trade license is missing. Please reapply with a valid Sijil Tijari document."
            className="w-full px-3 py-2.5 border border-border rounded-lg text-sm text-foreground bg-card placeholder:text-muted-foreground outline-none resize-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setRejectModal({ open: false, applicationId: '', name: '', reason: '' })}
            className="btn-ghost border border-border"
          >
            Cancel
          </button>
          <button
            onClick={handleReject}
            disabled={!rejectModal.reason.trim() || processingId === rejectModal.applicationId}
            className="btn-danger disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {processingId === rejectModal.applicationId ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <XCircle size={14} />
            )}
            Reject Application
          </button>
        </div>
      </Modal>

      {/* Request More Info Modal */}
      <Modal
        open={infoModal.open}
        onClose={() => setInfoModal({ open: false, applicationId: '', name: '', message: '' })}
        title="Request More Information"
        size="md"
      >
        <p className="text-sm text-muted-foreground mb-4">
          Send a message to <span className="font-600 text-foreground">{infoModal.name}</span> requesting additional documents.
        </p>
        <div className="mb-5">
          <label className="block text-sm font-600 text-foreground mb-1.5">
            Message to applicant <span className="text-red-500">*</span>
          </label>
          <textarea
            rows={4}
            value={infoModal.message}
            onChange={(e) => setInfoModal((p) => ({ ...p, message: e.target.value }))}
            placeholder="e.g. Please upload a clearer photo of your National ID and at least 2 additional portfolio photos."
            className="w-full px-3 py-2.5 border border-border rounded-lg text-sm text-foreground bg-card placeholder:text-muted-foreground outline-none resize-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setInfoModal({ open: false, applicationId: '', name: '', message: '' })}
            className="btn-ghost border border-border"
          >
            Cancel
          </button>
          <button
            onClick={handleRequestInfo}
            disabled={!infoModal.message.trim()}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <MessageSquare size={14} />
            Send Request
          </button>
        </div>
      </Modal>

      {/* Document Lightbox */}
      <Modal
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        title="Document Viewer"
        size="lg"
      >
        <div className="aspect-video bg-muted rounded-xl flex items-center justify-center">
          <div className="text-center">
            <ImageIcon size={40} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-sm font-600 text-foreground mb-1">Document preview</p>
            <p className="text-xs text-muted-foreground">
              In production, uploaded documents from Supabase Storage would render here.
            </p>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={() => setLightboxOpen(false)} className="btn-ghost border border-border">
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
}